import type { PropsWithChildren } from "react";
import { useAppShellStore, useBreadcrumbPagePaths } from "../../app-shell";
import { cn } from "../../utils";
import type { NavigationProps } from "../types";
import { SidebarNavigationItems } from "./sidebar-navigation-items";
import { SidebarNavigationMenu } from "./sidebar-navigation-menu";
import { SidebarNavigationTop } from "./sidebar-navigation-top";

export function SidebarNavigation({
	navigationItems,
	menuItems,
	children,
}: NavigationProps & PropsWithChildren) {
	/** Hooks */
	const { sidebarIsCollapsed } = useAppShellStore();
	// The breadcrumbs are drawn in the sidebar layout only, so the sidebar tells them where a
	// page exists.
	useBreadcrumbPagePaths(navigationItems);

	/** Computed */
	const sortedNavigationItems = [...navigationItems].sort((a, b) => {
		const aIsGroup = !!a.items?.length;
		const bIsGroup = !!b.items?.length;
		return Number(aIsGroup) - Number(bIsGroup);
	});

	return (
		<div
			className={cn(
				"rounded-base",
				"h-full bg-primary-50 dark:bg-primary-900/50 grid transition-[width] duration-200",
				"border-[0.5px] border-color mr-2",
				sidebarIsCollapsed ? "w-max" : "w-[225px]",
			)}
			style={{
				gridTemplateRows: "max-content 1fr max-content",
			}}
		>
			<SidebarNavigationTop />
			<SidebarNavigationItems navigationItems={sortedNavigationItems} />
			{/* Collapsed, the sidebar is as wide as its widest child, so the slot would hold it open. */}
			{!sidebarIsCollapsed && children}
			<SidebarNavigationMenu menuItems={menuItems} />
		</div>
	);
}
